import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconButton,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  DefaultButton,
  TextField,
  Dropdown,
  IDropdownOption,
} from "@fluentui/react";
import {
  getDatabases,
  searchXpressTraxCustomers,
  DatabaseResponse,
  XpressTraxCustomerResponse,
} from "../../api/cortexCloning";
import { resetPassword } from "../../api/passwordReset";

const PasswordResetPage = () => {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState("");

  // Company search
  const [companySearchTerm, setCompanySearchTerm] = useState("");
  const [companySearchResults, setCompanySearchResults] = useState<XpressTraxCustomerResponse[]>([]);
  const [loadingCompanySearch, setLoadingCompanySearch] = useState(false);
  const [companyId, setCompanyId] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [server, setServer] = useState("");

  // Database
  const [databases, setDatabases] = useState<DatabaseResponse[]>([]);
  const [selectedDatabase, setSelectedDatabase] = useState("");
  const [loadingDatabases, setLoadingDatabases] = useState(false);

  // Username
  const [username, setUsername] = useState("");

  // Debounced XpressTrax company search
  useEffect(() => {
    if (companySearchTerm.length < 2) {
      setCompanySearchResults([]);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoadingCompanySearch(true);
      try {
        const result = await searchXpressTraxCustomers(companySearchTerm);
        if (!cancelled) setCompanySearchResults(result.customers);
      } catch {
        if (!cancelled) setCompanySearchResults([]);
      } finally {
        if (!cancelled) setLoadingCompanySearch(false);
      }
    }, 300);

    return () => { cancelled = true; clearTimeout(timer); };
  }, [companySearchTerm]);

  // Auto-fetch databases when company and server are set
  useEffect(() => {
    const id = parseInt(companyId);
    if (!id || !server) {
      setDatabases([]);
      setSelectedDatabase("");
      return;
    }

    let cancelled = false;
    const fetchDatabases = async () => {
      setLoadingDatabases(true);
      try {
        const data = await getDatabases(id, server);
        if (cancelled) return;
        setDatabases(data);
        setSelectedDatabase("");
      } catch {
        if (cancelled) return;
        setErrorMessage("Failed to look up databases for this company/server");
        setDatabases([]);
      } finally {
        if (!cancelled) setLoadingDatabases(false);
      }
    };

    fetchDatabases();
    return () => { cancelled = true; };
  }, [companyId, server]);

  const databaseOptions: IDropdownOption[] = [...databases]
    .sort((a, b) => a.companyLetter.localeCompare(b.companyLetter))
    .map((db) => ({
      key: db.databaseName,
      text: `${db.companyLetter} - ${db.companyName}`,
    }));

  const handleClear = () => {
    setCompanyId("");
    setCompanyName("");
    setServer("");
    setDatabases([]);
    setSelectedDatabase("");
    setCompanySearchTerm("");
    setCompanySearchResults([]);
    setUsername("");
    setErrorMessage("");
    setTemporaryPassword("");
  };

  const handleSubmit = async () => {
    if (!companyId || !selectedDatabase || !username.trim()) {
      setErrorMessage("All fields are required");
      return;
    }

    const selectedDb = databases.find((db) => db.databaseName === selectedDatabase);
    if (!selectedDb) {
      setErrorMessage("Selected database not found — please re-select");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");
    setTemporaryPassword("");
    try {
      const result = await resetPassword(server, parseInt(companyId), selectedDb.companyLetter, username, selectedDb.companyName);
      setTemporaryPassword(result.password);
    } catch (error: any) {
      const detail = error?.response?.data || error?.message || "Unknown error";
      setErrorMessage(`Failed to reset password: ${typeof detail === "string" ? detail : JSON.stringify(detail)}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="page-header">
        <h2 className="page-title">Password Resets</h2>
      </div>

      <div style={{ padding: "20px 0 20px 24px", maxWidth: 560 }}>
        {/* Company search */}
        <div style={{ position: "relative", marginBottom: companyName ? 4 : 8 }}>
          <TextField
            label="Search Company"
            value={companySearchTerm}
            onChange={(_, value) => setCompanySearchTerm(value || "")}
            placeholder="Type company name to search..."
            iconProps={loadingCompanySearch ? { iconName: "ProgressRingDots" } : { iconName: "Search" }}
          />
          {companySearchResults.length > 0 && companySearchTerm.length >= 2 && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                maxHeight: 200,
                overflowY: "auto",
                backgroundColor: "#fff",
                border: "1px solid #edebe9",
                borderRadius: "0 0 4px 4px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                zIndex: 10,
              }}
            >
              {companySearchResults.map((c) => (
                <div
                  key={`${c.companyId}-${c.serverId}`}
                  onClick={() => {
                    setCompanyId(c.companyId.toString());
                    setCompanyName(c.companyName);
                    setServer(c.serverId ?? "");
                    setCompanySearchTerm("");
                    setCompanySearchResults([]);
                    setTemporaryPassword("");
                  }}
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontSize: 13,
                    borderBottom: "1px solid #f3f2f1",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f3f2f1")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#fff")}
                >
                  <div style={{ fontWeight: 600, color: "#323130" }}>{c.companyName}</div>
                  <div style={{ fontSize: 12, color: "#605e5c" }}>
                    ID: {c.companyId} — Server: {c.serverId ?? "N/A"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Selected company card */}
        {companyName && (
          <div
            style={{
              backgroundColor: "#f3f2f1",
              border: "1px solid #edebe9",
              borderRadius: 4,
              padding: "10px 14px",
              marginBottom: 8,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#323130" }}>{companyName}</div>
              <div style={{ fontSize: 12, color: "#605e5c", marginTop: 2 }}>
                ID: {companyId} — Server: {server}
              </div>
            </div>
            <IconButton
              iconProps={{ iconName: "Cancel" }}
              title="Clear selection"
              ariaLabel="Clear selection"
              styles={{ root: { width: 28, height: 28 }, icon: { fontSize: 12, color: "#605e5c" } }}
              onClick={() => {
                setCompanyId("");
                setCompanyName("");
                setServer("");
                setDatabases([]);
                setSelectedDatabase("");
                setTemporaryPassword("");
              }}
            />
          </div>
        )}

        {/* Database dropdown */}
        <Dropdown
          label="Database"
          options={databaseOptions}
          selectedKey={selectedDatabase}
          onChange={(_, option) => {
            setSelectedDatabase(option?.key as string);
            setTemporaryPassword("");
          }}
          required
          disabled={databases.length === 0 || loadingDatabases}
          placeholder={
            loadingDatabases
              ? "Loading..."
              : databases.length === 0
              ? "Search and select a company above"
              : "Select a database"
          }
          styles={{ root: { marginBottom: 16 } }}
        />

        {/* Username */}
        <TextField
          label="Username"
          value={username}
          onChange={(_, value) => {
            setUsername((value || "").toUpperCase());
            setTemporaryPassword("");
          }}
          placeholder="Enter username"
          required
          description="Username must be uppercase"
          styles={{ root: { marginBottom: 20 } }}
        />

        <div style={{ display: "flex", gap: 8, marginBottom: temporaryPassword ? 24 : 0 }}>
          <PrimaryButton
            text="Reset Password"
            onClick={handleSubmit}
            disabled={isSubmitting || !companyId || !selectedDatabase || !username.trim()}
          />
          <DefaultButton text="Clear" onClick={handleClear} />
          <DefaultButton
            iconProps={{ iconName: "ComplianceAudit" }}
            text="Audits"
            onClick={() => navigate("/password-reset/audits")}
            styles={{ root: { marginLeft: "auto" } }}
          />
        </div>

        {/* Temporary password result */}
        {temporaryPassword && (
          <div
            style={{
              marginTop: 24,
              backgroundColor: "#fff4ce",
              border: "1px solid #f0c84e",
              borderRadius: 4,
              padding: "16px 20px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <i className="ms-Icon ms-Icon--Completed" style={{ fontSize: 16, color: "#7a6100" }} />
              <span style={{ fontSize: 14, fontWeight: 600, color: "#7a6100" }}>
                Password Reset Successful
              </span>
            </div>
            <div style={{ fontSize: 13, color: "#605e5c", marginBottom: 6 }}>
              Temporary password for <strong>{username}</strong>:
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                backgroundColor: "#fff",
                border: "1px solid #edebe9",
                borderRadius: 4,
                padding: "8px 12px",
              }}
            >
              <span
                style={{
                  flex: 1,
                  fontFamily: "monospace",
                  fontSize: 16,
                  fontWeight: 600,
                  color: "#323130",
                  letterSpacing: "0.05em",
                }}
              >
                {temporaryPassword}
              </span>
              <IconButton
                iconProps={{ iconName: "Copy" }}
                title="Copy to clipboard"
                ariaLabel="Copy to clipboard"
                styles={{ root: { width: 32, height: 32 }, icon: { fontSize: 14, color: "#605e5c" } }}
                onClick={() => navigator.clipboard.writeText(temporaryPassword)}
              />
            </div>
            <div style={{ fontSize: 12, color: "#7a6100", marginTop: 10 }}>
              Share this with the user — they will be prompted to change it on next login.
            </div>
          </div>
        )}
      </div>

      {errorMessage && (
        <MessageBar
          messageBarType={MessageBarType.error}
          onDismiss={() => setErrorMessage("")}
          styles={{ root: { position: "fixed", bottom: 0, left: 0, width: "100%", zIndex: 1000 } }}
        >
          {errorMessage}
        </MessageBar>
      )}
    </>
  );
};

export default PasswordResetPage;
